<template>
    <div
        class="ai-voice-agent-call-starter"
        :class="{ 'hidden': !modalVisible }"
        v-tippy="{
            followCursor: true,
            placement: 'bottom-end',
        }"
        :content="$t('message.startAiCall')"
    >
        <!-- Ana buton -->
        <div v-if="!hideButton" class="conversation-actions-button ai-call" @click="showModal">
            <i class="material-icons voice-agent-icon">record_voice_over</i>
        </div>

        <!-- Arama modal -->
        <modal2
            v-if="modalVisible"
            :title="$t('message.aiCall')"
            @cancel="onModalClose"
            class="voice-agent-modal"
            :showFooter="false"
        >
            <a-spin :spinning="loading">
                <div class="call-form" v-if="hasVoiceAgentChannel">
                    <!-- Arama yapılacak numara seçimi -->
                    <div class="form-group">
                        <label>{{ $t("message.fromNumber") }}</label>
                        <a-select
                            v-model="selectedFromNumber"
                            :placeholder="$t('message.selectFromNumber')"
                            :loading="loadingNumbers"
                            style="width: 100%"
                        >
                            <a-select-option
                                v-for="number in phoneNumbers"
                                :key="number.phone_number"
                                :value="number.phone_number"
                            >
                                {{ number.nickname || number.phone_number_pretty || number.phone_number }}
                            </a-select-option>
                        </a-select>
                    </div>

                    <!-- Agent seçimi -->
                    <div class="form-group">
                        <label>{{ $t("message.aiVoiceAgent") }}</label>
                        <a-select
                            v-model="selectedAgentId"
                            :placeholder="$t('message.selectAiVoiceAgent')"
                            :loading="loadingAgents"
                            style="width: 100%"
                            allowClear
                        >
                            <a-select-option v-for="agent in agents" :key="agent.agent_id" :value="agent.agent_id">
                                {{ agent.agent_name }}
                            </a-select-option>
                        </a-select>
                        <div class="help-text">{{ $t("message.selectAiVoiceAgentHelpText") }}</div>
                    </div>

                    <!-- Aranacak numara -->
                    <div class="form-group">
                        <label>{{ $t("message.toNumber") }}</label>
                        <a-input 
                            v-model="toNumber" 
                            placeholder="+905xxxxxxxxx" 
                            :status="phoneNumberError ? 'error' : ''"
                        />
                        <div class="help-text" :class="{ 'error-text': phoneNumberError }">
                            {{ phoneNumberError || $t("message.toNumberHelpText") }}
                        </div>
                    </div>

                    <!-- Değişkenler bölümü -->
                    <div class="form-group variables-section" v-if="extractedVariables.length > 0">
                        <a-divider>{{ $t("message.aiVoiceAgentVariables") }}</a-divider>
                        <div v-for="(variable, index) in extractedVariables" :key="index" class="variable-row">
                            <span class="variable-name">{{ variable.name }}:</span>
                            <div class="variable-input-container">
                                <a-radio-group v-model="variable.bindType" class="bind-type">
                                    <a-radio value="static">{{ $t("message.aiVoiceAgentVariableManual") }}</a-radio>
                                    <a-radio value="dynamic">{{ $t("message.aiVoiceAgentVariableContact") }}</a-radio>
                                </a-radio-group>

                                <a-input
                                    v-if="variable.bindType === 'static'"
                                    v-model="variable.value"
                                    :placeholder="$t('message.aiVoiceAgentVariableValuePlaceholder')"
                                    class="variable-input"
                                />

                                <a-select
                                    v-else
                                    v-model="variable.contactField"
                                    :placeholder="$t('message.selectContactFieldPlaceholder')"
                                    class="variable-input"
                                    @change="updateVariableFromContact(variable)"
                                >
                                    <a-select-option value="fullname">{{ $t("chatbotv2.attr.name") }}</a-select-option>
                                    <a-select-option value="email">{{ $t("chatbotv2.attr.email") }}</a-select-option>
                                    <a-select-option value="phone">{{ $t("chatbotv2.attr.phone") }}</a-select-option>
                                    <a-select-option value="whatsappNumber">{{ $t("message.whatsapp") }}</a-select-option>
                                    <a-select-option value="city">{{ $t("chatbotv2.city") }}</a-select-option>
                                    <a-select-option value="country">{{ $t("chatbotv2.attr.country") }}</a-select-option>
                                </a-select>
                            </div>
                        </div>
                    </div>

                    <!-- İşlem düğmeleri -->
                    <div class="form-actions">
                        <a-button @click="modalVisible = false">{{ $t("message.cancelText") }}</a-button>
                        <a-button type="primary" @click="startCall" :loading="calling" :disabled="!isFormValid">
                            {{ $t("message.startCall") }}
                        </a-button>
                    </div>
                </div>
                <div class="upselling-container" v-else>
                    <div class="upselling-icon">
                        <i class="material-icons voice-agent-icon">record_voice_over</i>
                    </div>
                    <h3>{{ $t("message.aiVoiceAgentChannelRequired") }}</h3>
                    <p>{{ $t("message.aiVoiceAgentChannelRequiredDescription") }}</p>
                    <p>
                        {{ $t("message.aiVoiceAgentChannelRequiredDescription2") }}
                    </p>
                    <div class="upselling-features">
                        <div class="feature">
                            <i class="material-icons">check_circle</i>
                            <span>{{ $t("message.autoCallAnswer") }}</span>
                        </div>
                        <div class="feature">
                            <i class="material-icons">check_circle</i>
                            <span>{{ $t("message.customerService247") }}</span>
                        </div>
                        <div class="feature">
                            <i class="material-icons">check_circle</i>
                            <span>{{ $t("message.multiLanguageSupport") }}</span>
                        </div>
                    </div>
                    <div class="upselling-actions">
                        <a-button type="primary" @click="goToChannelSettings"> {{ $t("message.addAiVoiceAgentChannel") }} </a-button>
                    </div>
                </div>
            </a-spin>
        </modal2>

        <!-- Değişkenler butonu popover -->
        <a-popover
            v-model="variablePopoverVisible"
            :title="$t('message.aiVoiceAgentSpecialVariables')"
            trigger="click"
            placement="bottom"
            overlayClassName="variable-values-popover"
        >
            <template slot="content">
                <div v-if="extractedVariables.length === 0" class="no-variables">
                    <div v-html="$t('message.noVariablesFound')">
                    </div>
                </div>
                <div v-else class="variable-values-form">
                    <div v-for="(variable, index) in extractedVariables" :key="index" class="variable-row">
                        <span class="variable-name">{{ variable.name }}:</span>
                        <a-input v-model="variable.value" :placeholder="$t('message.aiVoiceAgentVariableValuePlaceholder')" />
                    </div>
                    <div class="form-actions">
                        <a-button type="primary" @click="saveVariableValues">{{ $t("message.save") }}</a-button>
                    </div>
                </div>
            </template>
        </a-popover>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../api/voiceAgent";
import { mapState } from "vuex";
import log2 from "../utils/log2";
import Modal2 from "./common/Modal2.vue";

const log = log2("[AiVoiceAgentCallStarter]");
const LOCALSTORAGE_KEY_FROM_NUMBER = "ai_call_last_from_number";
const LOCALSTORAGE_KEY_AGENT_ID = "ai_call_last_agent_id";

export default {
    name: "AiVoiceAgentCallStarter",
    components: {
        Modal2
    },
    props: {
        defaultToNumber: {
            type: String,
            default: "",
        },
        defaultFromNumber: {
            type: String,
            default: "",
        },
        defaultAgentId: {
            type: String,
            default: "",
        },
        variables: {
            type: Array,
            default: () => [],
        },
        contact: {
            type: Object,
            default: () => ({}),
        },
        hideButton: {
            type: Boolean,
            default: false,
        },
        autoOpen: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            modalVisible: false,
            variablePopoverVisible: false,
            loading: false,
            loadingNumbers: false,
            loadingAgents: false,
            calling: false,
            phoneNumbers: [],
            agents: [],
            selectedFromNumber: "",
            selectedAgentId: "",
            toNumber: "",
            overrideToNumber: false,
            extractedVariables: [],
            dynamicVariables: {},
            agent: null,
            selectedNumber: null,
            phoneNumberError: "",
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            channels: (state) => state.channel.channels,
        }),
        isFormValid() {
            return this.selectedFromNumber && 
                   this.toNumber && 
                   !this.phoneNumberError;
        },
        hasVoiceAgentChannel() {
            return this.channels.some((channel) => channel.type === "retell" && channel.enabled);
        },
    },
    watch: {
        selectedFromNumber(newValue) {
            if (newValue) {
                // Numaraya ait ajanı bulma
                const phoneNumber = this.phoneNumbers.find((n) => n.phone_number === newValue);
                this.selectedNumber = phoneNumber;

                // Eğer seçili bir ajan yoksa ve numaranın giden ajanı varsa, onu seç
                if (!this.selectedAgentId && phoneNumber && phoneNumber.outbound_agent_id) {
                    this.selectedAgentId = phoneNumber.outbound_agent_id;
                    // Ajanın detaylarını yükle
                    this.loadAgentDetails(phoneNumber.outbound_agent_id);
                }

                // Local storage'a kaydet
                localStorage.setItem(LOCALSTORAGE_KEY_FROM_NUMBER, newValue);
            }
        },
        selectedAgentId(newValue) {
            if (newValue) {
                // Ajanın detaylarını yükle
                this.loadAgentDetails(newValue);

                // Local storage'a kaydet
                localStorage.setItem(LOCALSTORAGE_KEY_AGENT_ID, newValue);
            }
        },
        modalVisible(newValue) {
            if (newValue) {
                this.loadData();

                if (this.defaultToNumber && !this.overrideToNumber) {
                    this.toNumber = this.defaultToNumber;
                }
            }
        },
        toNumber(newValue) {
            this.validatePhoneNumber(newValue);
        },
    },
    mounted() {
        // Kayıtlı değerleri localStorage'dan yükleme
        const savedFromNumber = localStorage.getItem(LOCALSTORAGE_KEY_FROM_NUMBER);
        const savedAgentId = localStorage.getItem(LOCALSTORAGE_KEY_AGENT_ID);

        // Props'dan veya localStorage'dan gelen değerleri kullan
        this.selectedFromNumber = this.defaultFromNumber || savedFromNumber || "";
        this.selectedAgentId = this.defaultAgentId || savedAgentId || "";
        this.toNumber = this.defaultToNumber || "";

        // Props'dan gelen değişkenleri kullan
        if (this.variables && this.variables.length > 0) {
            this.extractedVariables = this.variables.map((v) => ({
                name: v.label,
                value: v.value || "",
                bindType: "static",
                contactField: null,
            }));
        }

        // Contact prop'undan değişkenleri oluştur
        this.prepareContactVariables();

        // Eğer autoOpen prop'u true ise otomatik olarak modülü aç
        if (this.autoOpen && this.selectedAgentId) {
            this.$nextTick(() => {
                this.showModal();
            });
        }
    },
    methods: {
        onModalClose() {
            this.modalVisible = false;
            this.$emit("close");
        },
        async showModal() {
            this.modalVisible = true;
        },

        goToChannelSettings() {
            this.modalVisible = false;
            this.$router.push("/integrations/voice-agent");
        },

        async loadData() {
            this.loading = true;

            try {
                if (this.hasVoiceAgentChannel) {
                    await Promise.all([this.loadPhoneNumbers(), this.loadAgents()]);
                }

                // Contact değişkenlerini hazırla
                this.prepareContactVariables();
            } catch (error) {
                console.error("Veri yüklenirken hata oluştu:", error);
                this.$message.error(this.$t("message.errorLoadingData"));
            } finally {
                this.loading = false;
            }
        },

        async loadPhoneNumbers() {
            this.loadingNumbers = true;

            try {
                const phoneNumbers = await VOICE_AGENT_API.numbers.list(true);
                this.phoneNumbers = phoneNumbers;

                // Eğer hiç numara yoksa veya seçili numara yoksa
                if (phoneNumbers.length > 0 && !this.selectedFromNumber) {
                    this.selectedFromNumber = phoneNumbers[0].phone_number;
                }

                // Eğer defaultFromNumber varsa, onu seç
                if (this.defaultFromNumber) {
                    const found = phoneNumbers.find((n) => n.phone_number === this.defaultFromNumber);
                    if (found) {
                        this.selectedFromNumber = this.defaultFromNumber;
                    }
                }
            } catch (error) {
                console.error("Telefon numaraları yüklenirken hata oluştu:", error);
                this.$message.error(this.$t("message.errorLoadingDataPhoneNumbers"));
            } finally {
                this.loadingNumbers = false;
            }
        },

        async loadAgents() {
            this.loadingAgents = true;

            try {
                const agents = await VOICE_AGENT_API.agents.list(true);
                this.agents = agents;

                // Eğer default agent id varsa, onu seç
                if (this.defaultAgentId) {
                    const found = agents.find((a) => a.agent_id === this.defaultAgentId);
                    if (found) {
                        this.selectedAgentId = this.defaultAgentId;
                        await this.loadAgentDetails(this.defaultAgentId);
                    }
                }
            } catch (error) {
                console.error("Ajanlar yüklenirken hata oluştu:", error);
                this.$message.error(this.$t("message.errorLoadingAgents"));
            } finally {
                this.loadingAgents = false;
            }
        },

        async loadAgentDetails(agentId) {
            if (!agentId) return;

            try {
                const agent = await VOICE_AGENT_API.agents.get(agentId,true);
                this.agent = agent;

                // Ajana ait prompt'tan değişkenleri çıkar
                if (agent?.llm?.general_prompt) {
                    this.extractVariablesFromPrompt(agent.llm.general_prompt);
                }
            } catch (error) {
                console.error("Ajan detayları yüklenirken hata oluştu:", error);
            }
        },

        prepareContactVariables() {
            if (!this.contact) return;

            // Mevcut değişkenlerin bind tiplerini ve contact fieldlarını koru
            const contactVariables = [];

            // Mevcut değişkenlere contact değişkenlerini ekle veya güncelle
            contactVariables.forEach((variable) => {
                const existingIndex = this.extractedVariables.findIndex((v) => v.name === variable.name);
                if (existingIndex >= 0) {
                    if (!this.extractedVariables[existingIndex].value) {
                        this.extractedVariables[existingIndex].value = variable.value;
                    }
                } else {
                    this.extractedVariables.push(variable);
                }
            });
        },

        extractVariablesFromPrompt(promptText) {
            if (!promptText) return;

            // Sistem değişkenlerinin listesi (bunları gösterme)
            const systemVariables = [
                "current_agent_state",
                "previous_agent_state",
                "current_time",
                "current_time_Europe/Istanbul",
                "call_type",
                "direction",
                "user_number",
                "agent_number",
            ];

            // {{değişken_adı}} formatındaki tüm değişkenleri bul
            const regex = /\{\{([^}]+)\}\}/g;
            const matches = [...promptText.matchAll(regex)];

            // Değişken listesini oluştur
            const variables = [];
            const variableSet = new Set(); // Tekrar eden değişkenleri önlemek için

            matches.forEach((match) => {
                const varName = match[1].trim();

                // Sistem değişkenlerini dahil etme
                if (!systemVariables.includes(varName) && !variableSet.has(varName)) {
                    variableSet.add(varName);
                    // Mevcut değişkenlerde varsa onun değerini ve tipini kullan
                    const existingVar = this.extractedVariables.find((v) => v.name === varName);

                    variables.push({
                        name: varName,
                        value: existingVar ? existingVar.value : "",
                        bindType: existingVar ? existingVar.bindType : "static",
                        contactField: existingVar ? existingVar.contactField : null,
                    });
                }
            });

            // Sadece prompt'ta bulunan değişkenleri göster
            this.extractedVariables = variables;

            // Kişi bilgisi ile otomatik eşleştirme yap
            this.autoMapContactFields();
        },

        handleOverrideChange(e) {
            if (e.target.checked) {
                this.toNumber = this.defaultToNumber || "";
            } else {
                this.toNumber = this.defaultToNumber;
            }
        },

        saveVariableValues() {
            this.dynamicVariables = {};
            this.extractedVariables.forEach((variable) => {
                if (variable.value) {
                    this.dynamicVariables[variable.name] = variable.value;
                }
            });

            this.variablePopoverVisible = false;
        },

        async startCall() {
            if (!this.isFormValid) {
                this.$message.warning(this.$t("message.pleaseFillRequiredFields"));
                return;
            }

            this.calling = true;

            try {
                // Değişkenleri hazırla
                const dynamicVariables = {};
                this.extractedVariables.forEach((variable) => {
                    if (variable.value) {
                        dynamicVariables[variable.name] = variable.value;
                    }
                });

                // Arama verilerini oluştur
                const callData = {
                    fromNumber: this.selectedFromNumber,
                    toNumber: this.toNumber,
                    metadata: {
                        siteId: this.site._id,
                        userId: this.user._id,
                        source: "chat-screen",
                        contactId: this.contact._id,
                        whatsappNumber: this.contact.whatsappNumber,
                        channelId: this.contact.channel,
                    },
                };

                // Eğer contact prop'u varsa, metadata'ya ekle
                if (this.contact && this.contact._id) {
                    callData.metadata.contactId = this.contact._id;

                    if (this.contact.lastConversation && this.contact.lastConversation._id) {
                        callData.metadata.conversationId = this.contact.lastConversation._id;
                    }
                }

                // Eğer ajan seçilmişse ekle
                if (this.selectedAgentId) {
                    callData.overrideAgentId = this.selectedAgentId;
                }

                // Değişkenler varsa ekle
                if (Object.keys(dynamicVariables).length > 0) {
                    callData.retellLlmDynamicVariables = dynamicVariables;
                }

                log("Arama başlatılıyor:", callData);

                // Aramayı başlat
                await VOICE_AGENT_API.calls.createPhone(callData);

                // Başarılı mesajı göster
                this.$message.success(this.$t("message.callStartedSuccessfully"));

                // Modal'ı kapat
                this.modalVisible = false;
            } catch (error) {
                console.error("Arama başlatılırken hata oluştu:", error);
                this.$message.error(this.$t("message.errorStartingCall"));
            } finally {
                this.calling = false;
            }
        },

        // Kişi alanlarını otomatik eşleştirme
        autoMapContactFields() {
            // İsim için eşleştirme
            const nameVars = this.extractedVariables.filter(
                (v) =>
                    v.name.toLowerCase().includes("name") ||
                    v.name.toLowerCase().includes("ad") ||
                    v.name.toLowerCase().includes("isim")
            );

            // E-posta için eşleştirme
            const emailVars = this.extractedVariables.filter(
                (v) =>
                    v.name.toLowerCase().includes("email") ||
                    v.name.toLowerCase().includes("mail") ||
                    v.name.toLowerCase().includes("eposta")
            );

            // Telefon için eşleştirme
            const phoneVars = this.extractedVariables.filter(
                (v) =>
                    v.name.toLowerCase().includes("phone") ||
                    v.name.toLowerCase().includes("telefon") ||
                    v.name.toLowerCase().includes("number")
            );

            // Eşleştirmeleri uygula
            nameVars.forEach((v) => {
                if (this.contact.fullname) {
                    v.bindType = "dynamic";
                    v.contactField = "fullname";
                    v.value = this.contact.fullname;
                }
            });

            emailVars.forEach((v) => {
                if (this.contact.email) {
                    v.bindType = "dynamic";
                    v.contactField = "email";
                    v.value = this.contact.email;
                }
            });

            phoneVars.forEach((v) => {
                const phone = this.contact.phone || this.contact.whatsappNumber;
                if (phone) {
                    v.bindType = "dynamic";
                    v.contactField = this.contact.phone ? "phone" : "whatsappNumber";
                    v.value = phone;
                }
            });
        },

        // Kişi bilgisi değiştiğinde değişken değerini güncelle
        updateVariableFromContact(variable) {
            if (variable.contactField && this.contact) {
                variable.value = this.contact[variable.contactField] || "";
            }
        },

        validatePhoneNumber(number) {
            // E.164 formatı için regex: + ile başlayan, ülke kodu ve telefon numarası
            const e164Regex = /^\+[1-9]\d{1,14}$/;
            
            if (!number) {
                this.phoneNumberError = "";
                return;
            }

            if (!e164Regex.test(number)) {
                this.phoneNumberError = this.$t("message.toNumberHelpText");
            } else {
                this.phoneNumberError = "";
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.ai-voice-agent-call-starter {
    position: relative;
    display: inline-block;
    align-items: center;
    &.hidden {
        display: none;
    }

    .conversation-actions-button {
        cursor: pointer;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        user-select: none;

        .material-icons {
            color: var(--text-main-default);
            font-size: 20px;
        }

        &.ai-call {
            .material-icons.voice-agent-icon {
                background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1, #96c93d, #ffbe0b);
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: rainbow 5s ease infinite;
            }
        }
    }
}

.upselling-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;

    .upselling-icon {
        font-size: 50px;
        margin-bottom: 20px;

        .voice-agent-icon {
            font-size: 50px;
            background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1, #96c93d, #ffbe0b);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: rainbow 5s ease infinite;
        }
    }

    h3 {
        font-size: 20px;
        margin-bottom: 10px;
        font-weight: 500;
    }

    p {
        margin-bottom: 15px;
        color: #666;
    }

    .upselling-features {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin: 20px 0;
        width: 100%;
        max-width: 300px;

        .feature {
            display: flex;
            align-items: center;
            margin-bottom: 10px;

            .material-icons {
                color: #4ecdc4;
                margin-right: 10px;
            }
        }
    }

    .upselling-actions {
        margin-top: 20px;
    }
}

@keyframes rainbow {
    0% {
        filter: hue-rotate(0deg);
    }
    100% {
        filter: hue-rotate(360deg);
    }
}
</style>
<style lang="scss">
.call-form {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        label {
            font-weight: 500;
            font-size: 14px;
            color: #333;
        }

        .help-text {
            font-size: 12px;
            color: #8c8c8c;
            margin-top: 4px;

            &.error-text {
                color: #ff4d4f;
            }
        }
    }

    .variables-section {
        margin-top: 8px;

        .variable-row {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-bottom: 15px;

            .variable-name {
                min-width: 120px;
                font-weight: 500;
                padding-top: 8px;
            }

            .variable-input-container {
                display: flex;
                flex-direction: column;
                gap: 5px;
                flex: 1;

                .bind-type {
                    margin-bottom: 5px;
                }

                .variable-input {
                    width: 100%;
                }
            }
        }
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 16px;
    }
}
.variable-values-popover {
    min-width: 350px;
    max-width: 500px;

    .popover-title {
        font-weight: 500;
    }

    .variable-values-form {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .variable-row {
            display: flex;
            align-items: center;
            gap: 10px;

            .variable-name {
                min-width: 120px;
                font-weight: 500;
            }
        }

        .form-actions {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
        }
    }

    .no-variables {
        padding: 20px 0;
        color: #999;
        font-style: italic;
        text-align: center;
    }
}
</style>
